import { toggleAsset } from '@/model/ltcFinancialPlanningModel';
import { cn } from '@client/lib';
import { AppState } from '@client/model/store';
import { Check } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';

export interface AssetOption {
  type: LtcFinancialPlanningAssetType;
  label: string;
}

export const ASSET_OPTIONS: AssetOption[] = [
  { type: 'rothIra', label: 'Roth IRA' },
  { type: 'roth401k', label: 'Roth 401(k)' },
  { type: 'traditionalIra', label: 'Traditional IRA' },
  { type: 'traditional401k', label: 'Traditional 401(k)' },
  { type: 'hsa', label: 'HSA Account' },
  { type: 'brokerage', label: 'Brokerage Account' },
  { type: 'homeEquity', label: 'Home Equity' },
  { type: 'retirementIncome', label: 'Retirement Income' },
];

interface AssetSelectionGridProps {
  className?: string;
}

export function AssetSelectionButton(props: {
  asset: AssetOption;
  isSelected: boolean;
  onToggleAsset: (assetType: LtcFinancialPlanningAssetType) => void;
}) {
  const { asset, isSelected, onToggleAsset } = props;

  return (
    <button
      type="button"
      onClick={() => onToggleAsset(asset.type)}
      className={cn(
        'relative flex items-center justify-between rounded-lg border-2 px-4 py-4 text-left transition-all hover:shadow-md',
        {
          'border-primary bg-primary/5': isSelected,
          'border-gray-200 bg-white hover:border-gray-300': !isSelected,
        },
      )}
    >
      <span
        className={cn('text-base font-medium', {
          'text-gray-900': isSelected,
          'text-gray-700': !isSelected,
        })}
      >
        {asset.label}
      </span>

      <div
        className={cn(
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border-2 transition-colors',
          {
            'border-primary bg-primary': isSelected,
            'border-gray-300 bg-white': !isSelected,
          },
        )}
      >
        {isSelected && (
          <Check
            className="h-4 w-4 text-white"
            strokeWidth={3}
          />
        )}
      </div>
    </button>
  );
}

export function AssetSelectionGrid({ className }: AssetSelectionGridProps) {
  const dispatch = useDispatch();

  const handleToggleAsset = (assetType: LtcFinancialPlanningAssetType) => {
    dispatch(toggleAsset(assetType));
  };

  const { selectedAssets } = useSelector(
    (state: { ltcFinancialPlanning: AppState['ltcFinancialPlanning'] }) =>
      state.ltcFinancialPlanning,
  );

  return (
    <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3', className)}>
      {ASSET_OPTIONS.map(asset => (
        <AssetSelectionButton
          key={asset.type}
          asset={asset}
          isSelected={selectedAssets.includes(asset.type)}
          onToggleAsset={handleToggleAsset}
        />
      ))}
    </div>
  );
}
